import { Colors, FiltersTextBtn, Media, SelectIndents } from 'enums';
import { ICountry, ILocation } from 'interfaces';
import { StylesConfig } from 'react-select';

const mediaNotebook = `@media ${Media.notebook}`;

const checkHover = (
  isFocused: boolean,
  hasValue: boolean,
  menuIsOpen: boolean,
) => {
  if (!isFocused && !hasValue && !menuIsOpen) {
    return '1px solid transparent';
  }

  if (!isFocused && hasValue && !menuIsOpen) {
    return '1px solid transparent';
  }

  return `1px solid ${Colors.yellow1}`;
};

const getControlImageFilter = ({
  isFocused,
  hasValue,
  isDisabled,
}: {
  isFocused: boolean;
  hasValue: boolean;
  isDisabled: boolean;
}) => {
  if (isDisabled) {
    return FiltersTextBtn.gray4;
  }

  if (isFocused || hasValue) {
    return 'brightness(0) saturate(100%) invert(17%) sepia(1%) saturate(2554%) hue-rotate(11deg) brightness(97%) contrast(85%)';
  }

  return 'none';
};

const getSelectStyles = <T extends ILocation | ICountry>(): StylesConfig<
  T,
  false
> => ({
  container: (base) => ({
    ...base,
    margin: '30px 0 0 0',
    width: '100%',
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    cursor: 'pointer',
    textAlign: 'left',
    borderRadius: 10,
    [mediaNotebook]: {
      fontSize: 16,
    },
  }),
  control: (base, { isFocused, hasValue, menuIsOpen, isDisabled }) => ({
    ...base,
    flexDirection: 'row',
    height: 'auto',
    padding: `${SelectIndents.mobile.paddingTop} 
    ${SelectIndents.mobile.paddingRight} 
    ${SelectIndents.mobile.paddingBottom} 
    ${SelectIndents.mobile.paddingLeft}`,
    border: checkHover(isFocused, hasValue, menuIsOpen),
    boxShadow: 'none',
    backgroundColor: Colors.white,
    borderRadius: 10,
    cursor: 'pointer',
    '&:hover': {
      border: `1px solid ${isFocused ? Colors.yellow1 : Colors.gray3}`,
    },
    [mediaNotebook]: {
      backgroundColor: isFocused ? Colors.white : Colors.background,
      height: 'auto',
      padding: `${SelectIndents.notebook.paddingTop} 
      ${SelectIndents.notebook.paddingRight} 
      ${SelectIndents.notebook.paddingBottom} 
      ${SelectIndents.notebook.paddingLeft}`,
    },
    img: {
      filter: getControlImageFilter({ isFocused, hasValue, isDisabled }),
    },
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 210,
    '::-webkit-scrollbar': {
      width: 4,
    },
    '::-webkit-scrollbar-thumb': {
      background: Colors.yellow1,
      borderRadius: 2,
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: Colors.yellow2,
    },
  }),
  option: (base, { isSelected }) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: isSelected ? Colors.yellow1 : Colors.white,
    color: Colors.gray1,
    padding: 16,
    cursor: 'pointer',
    '&:active': {
      backgroundColor: 'transparent',
    },
    '&:hover': {
      backgroundColor: Colors.background,
    },
  }),
  placeholder: (base, state) => ({
    ...base,
    display: state.isFocused ? 'none' : 'block',
    color: state.isDisabled ? Colors.gray3 : base.color,
    userSelect: 'none',
    fontSize: 13,
  }),
  valueContainer: (base) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    padding: '0px !important',
    gap: 10,

    '& img': {
      width: '22px !important',
      height: '22px !important',

      [`@media ${Media.notebook}`]: {
        width: '30px !important',
        height: '30px !important',
      },

      [`@media ${Media.mobile}`]: {
        width: 18,
        height: 18,
      },
    },

    '& input': {
      opacity: '1 !important',
    },

    [mediaNotebook]: {
      padding: '0px 14px',
      gap: 14,
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    display: 'flex',
    alignItems: 'center',
    padding: '0px 16px',
    height: 64,
    textAlign: 'left',
    color: Colors.gray1,
  }),
  input: (base, { selectProps, value }) => ({
    ...base,
    position: value || selectProps.menuIsOpen ? 'inherit' : 'absolute',
  }),
});

export const CitySelectStyles = getSelectStyles<ILocation>();
export const CountrySelectStyles = getSelectStyles<ICountry>();
